import { useCallback, useEffect, useState } from "react";
import { api, hrDateTime, downloadAsBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { AlertTriangle, RefreshCw, Search, X, Trash2, ShieldAlert, Loader2, Tag, UserCog, Plus, Pencil, Clock, Stamp, Upload, CheckCircle2, FileImage, FileSpreadsheet, DatabaseBackup } from "lucide-react";
import { toast } from "sonner";
import { DEFAULT_ROLE_LABELS, ROLE_OPTIONS, applyRoleLabels } from "../lib/api";
import { ReminderHistoryTab } from "../components/ReminderHistoryTab";

const ACTION_LABEL = {
  created: "Predaja GO",
  created_on_behalf: "Predaja u ime",
  approved: "Suglasan od voditelja",
  rejected: "Odbijeno",
  wfh_created: "Predaja WFH",
  wfh_created_on_behalf: "Predaja WFH u ime",
  wfh_approved: "Suglasan od voditelja WFH",
  wfh_rejected: "Odbijen WFH",
  pdf_generated: "Generiran PDF",
  sent: "Poslano",
  system_reset: "RESET podataka",
};

function StampPreview({ exists, endpoint = "/admin/dean-stamp/preview", testid = "stamp-preview", maxHeight = 128, maxWidth = 280 }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoked = null;
    if (!exists) { setSrc(null); return; }
    api.get(endpoint, { responseType: "blob" })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        revoked = url;
        setSrc(url);
      })
      .catch(() => setSrc(null));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [exists, endpoint]);
  if (!exists || !src) return <div className="text-xs text-muted-foreground">Učitavanje pregleda…</div>;
  return (
    <img
      src={src}
      alt="Pregled"
      style={{ maxHeight, maxWidth }}
      className="object-contain"
      data-testid={testid}
    />
  );
}

export default function Administracija() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";

  // ---- Audit ----
  const [audit, setAudit] = useState([]);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({ actor: "", action: "all" });
  const [loadingAudit, setLoadingAudit] = useState(false);
  const currentYear = new Date().getFullYear();
  const [auditExportYear, setAuditExportYear] = useState(currentYear);
  const [auditExporting, setAuditExporting] = useState(false);

  const downloadAuditXlsx = async () => {
    setAuditExporting(true);
    try {
      await downloadAsBlob(
        `/export/audit.xlsx?year=${auditExportYear}`,
        `Audit_log_${auditExportYear}.xlsx`,
      );
      toast.success(`Audit log za ${auditExportYear} preuzet`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri preuzimanju");
    } finally {
      setAuditExporting(false);
    }
  };

  const loadAudit = useCallback(async () => {
    setLoadingAudit(true);
    try {
      const params = {};
      if (filters.actor.trim()) params.actor = filters.actor.trim();
      if (filters.action && filters.action !== "all") params.action = filters.action;
      const r = await api.get("/admin/audit", { params });
      setAudit(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setLoadingAudit(false); }
  }, [filters]);

  useEffect(() => {
    api.get("/admin/audit/actions").then(r => setActions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  // ---- Reset ----
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetting, setResetting] = useState(false);

  // ---- Role labels ----
  const [labels, setLabels] = useState({});
  const [savingLabels, setSavingLabels] = useState(false);

  // ---- Custom roles ----
  const [customRoles, setCustomRoles] = useState([]);
  const [crLoading, setCrLoading] = useState(false);
  const [crDialog, setCrDialog] = useState(null); // null | "new" | {id, ...}
  const [crForm, setCrForm] = useState({ name: "", description: "", base_role: "employee_nonteaching" });
  const [crBusy, setCrBusy] = useState(false);
  const [crDeleteFor, setCrDeleteFor] = useState(null); // {id, name, assigned_count}
  const [crDeleteForce, setCrDeleteForce] = useState(false);
  const canManageCustomRoles = ["admin", "secretary", "prodekan"].includes(user.role);

  const loadCustomRoles = useCallback(async () => {
    setCrLoading(true);
    try {
      const r = await api.get("/admin/custom-roles");
      setCustomRoles(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setCrLoading(false); }
  }, []);
  useEffect(() => { loadCustomRoles(); }, [loadCustomRoles]);

  // ---- Work schedules (radni rasporedi) ----
  const [workSchedules, setWorkSchedules] = useState([]);
  const [wsLoading, setWsLoading] = useState(false);
  const [wsDialog, setWsDialog] = useState(null); // null | "new" | {id, ...}
  const [wsForm, setWsForm] = useState({ name: "", start_time: "07:00", end_time: "15:00", max_late_min: 15, flexible: false });
  const [wsBusy, setWsBusy] = useState(false);
  const [wsDeleteFor, setWsDeleteFor] = useState(null);
  const canManageSchedules = canManageCustomRoles; // same permission group

  const loadWorkSchedules = useCallback(async () => {
    setWsLoading(true);
    try {
      const r = await api.get("/work-schedules");
      setWorkSchedules(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setWsLoading(false); }
  }, []);
  useEffect(() => { loadWorkSchedules(); }, [loadWorkSchedules]);

  const openWsNew = () => {
    setWsForm({ name: "", start_time: "07:00", end_time: "15:00", max_late_min: 15, flexible: false });
    setWsDialog("new");
  };
  const openWsEdit = (s) => {
    setWsForm({
      name: s.name,
      start_time: s.start_time || "",
      end_time: s.end_time || "",
      max_late_min: s.max_late_min ?? 15,
      flexible: !!s.flexible,
    });
    setWsDialog(s);
  };
  const saveWorkSchedule = async () => {
    if (!wsForm.name?.trim()) { toast.error("Naziv je obavezan"); return; }
    setWsBusy(true);
    try {
      const payload = { ...wsForm };
      if (payload.flexible) { payload.start_time = ""; payload.end_time = ""; }
      if (wsDialog === "new") {
        await api.post("/admin/work-schedules", payload);
        toast.success("Raspored kreiran");
      } else {
        await api.put(`/admin/work-schedules/${wsDialog.id}`, payload);
        toast.success("Raspored ažuriran");
      }
      setWsDialog(null);
      loadWorkSchedules();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setWsBusy(false); }
  };
  const confirmDeleteWs = async () => {
    if (!wsDeleteFor) return;
    setWsBusy(true);
    try {
      await api.delete(`/admin/work-schedules/${wsDeleteFor.id}`);
      toast.success("Raspored obrisan");
      setWsDeleteFor(null);
      loadWorkSchedules();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setWsBusy(false); }
  };

  const openCrNew = () => {
    setCrForm({ name: "", description: "", base_role: "employee_nonteaching" });
    setCrDialog("new");
  };
  const openCrEdit = (cr) => {
    setCrForm({ name: cr.name, description: cr.description || "", base_role: cr.base_role });
    setCrDialog(cr);
  };
  const saveCustomRole = async () => {
    if (!crForm.name?.trim()) { toast.error("Naziv je obavezan"); return; }
    setCrBusy(true);
    try {
      if (crDialog === "new") {
        await api.post("/admin/custom-roles", crForm);
        toast.success("Uloga kreirana");
      } else {
        await api.put(`/admin/custom-roles/${crDialog.id}`, crForm);
        toast.success("Uloga ažurirana");
      }
      setCrDialog(null);
      loadCustomRoles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setCrBusy(false); }
  };
  const confirmDeleteCr = async () => {
    if (!crDeleteFor) return;
    setCrBusy(true);
    try {
      const params = crDeleteForce ? { force: true } : {};
      const r = await api.delete(`/admin/custom-roles/${crDeleteFor.id}`, { params });
      toast.success(`Uloga obrisana${r.data.unassigned ? ` (${r.data.unassigned} korisnika odjavljeno)` : ""}`);
      setCrDeleteFor(null); setCrDeleteForce(false);
      loadCustomRoles();
    } catch (e) {
      const status = e.response?.status;
      if (status === 409) {
        setCrDeleteForce(true);
        toast.error(e.response?.data?.detail || "Uloga je dodijeljena. Ponovo kliknite za prisilno brisanje.");
      } else {
        toast.error(e.response?.data?.detail || "Greška");
      }
    } finally { setCrBusy(false); }
  };

  useEffect(() => {
    api.get("/settings/role-labels").then(r => setLabels(r.data || {})).catch(() => {});
  }, []);

  const saveLabels = async () => {
    setSavingLabels(true);
    try {
      const r = await api.put("/settings/role-labels", { labels });
      applyRoleLabels(r.data.labels);
      toast.success("Nazivi uloga spremljeni. Promjene su odmah aktivne za novo otvaranje stranica.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setSavingLabels(false); }
  };

  const resetToDefaults = () => {
    setLabels({});
    toast.info("Vraćeno na zadane vrijednosti. Kliknite 'Spremi' da potvrdite.");
  };

  const doReset = async () => {
    setResetting(true);
    try {
      const r = await api.post("/admin/reset-requests", { confirm: resetPhrase });
      toast.success(
        `Obrisano: ${r.data.deleted_vacation_requests} GO, ${r.data.deleted_wfh_requests} WFH, ${r.data.deleted_audit_entries} audit` +
        (r.data.snapshot_restored ? " · baza vraćena na snapshot ✓" : " · snapshot NIJE spremljen (baza zaposlenika netaknuta)")
      );
      setResetOpen(false); setResetPhrase("");
      loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setResetting(false); }
  };

  // ---- Iter68: Snapshot osnovne baze ----
  const [snapInfo, setSnapInfo] = useState(null);
  const [snapSaving, setSnapSaving] = useState(false);
  const loadSnapshot = useCallback(async () => {
    try { const r = await api.get("/admin/snapshot"); setSnapInfo(r.data); }
    catch (e) { console.debug("[Administracija] loadSnapshot failed:", e?.message); }
  }, []);
  useEffect(() => { if (isAdmin) loadSnapshot(); }, [isAdmin, loadSnapshot]);
  const saveSnapshot = async () => {
    setSnapSaving(true);
    try {
      const r = await api.post("/admin/snapshot");
      toast.success(`Snapshot spremljen: ${r.data.counts.users} zaposlenika, ${r.data.counts.custom_roles} custom rola. Za produkciju napravite Deploy.`);
      loadSnapshot();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setSnapSaving(false); }
  };

  // ---- Dean stamp ----
  const [stampExists, setStampExists] = useState(false);
  const [stampBusy, setStampBusy] = useState(false);
  const [stampDeleteOpen, setStampDeleteOpen] = useState(false);
  const canManageStamp = ["admin", "secretary"].includes(user.role);

  const refreshStampStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/dean-stamp/exists");
      setStampExists(!!r.data.exists);
    } catch (e) { console.debug("[Administracija] refreshStampStatus failed:", e?.message); }
  }, []);
  useEffect(() => { refreshStampStatus(); }, [refreshStampStatus]);

  const uploadStamp = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Datoteka mora biti do 2MB.");
      return;
    }
    if (!/\.(png|jpe?g)$/i.test(file.name)) {
      toast.error("Dozvoljeni formati: PNG ili JPG.");
      return;
    }
    setStampBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/dean-stamp", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pečat dekana spremljen. Bit će automatski korišten na sljedećim PDF rješenjima.");
      refreshStampStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri uploadu");
    } finally { setStampBusy(false); }
  };

  const deleteStamp = async () => {
    setStampBusy(true);
    try {
      await api.delete("/admin/dean-stamp");
      toast.success("Pečat dekana uklonjen.");
      setStampDeleteOpen(false);
      refreshStampStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setStampBusy(false); }
  };

  // ---- Memorandum (letterhead) ----
  const [memoExists, setMemoExists] = useState(false);
  const [memoBusy, setMemoBusy] = useState(false);
  const [memoDeleteOpen, setMemoDeleteOpen] = useState(false);

  const refreshMemoStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/memorandum/exists");
      setMemoExists(!!r.data.exists);
    } catch (e) { console.debug("[Administracija] refreshMemoStatus failed:", e?.message); }
  }, []);
  useEffect(() => { refreshMemoStatus(); }, [refreshMemoStatus]);

  const uploadMemo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Datoteka mora biti do 5MB.");
      return;
    }
    if (!/\.(png|jpe?g)$/i.test(file.name)) {
      toast.error("Dozvoljeni formati: PNG ili JPG.");
      return;
    }
    setMemoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/memorandum", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Memorandum spremljen. Bit će korišten kao zaglavlje na svim PDF rješenjima.");
      refreshMemoStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri uploadu");
    } finally { setMemoBusy(false); }
  };

  const deleteMemo = async () => {
    setMemoBusy(true);
    try {
      await api.delete("/admin/memorandum");
      toast.success("Memorandum uklonjen. PDF zaglavlje vraćeno na zadano (KIF logo).");
      setMemoDeleteOpen(false);
      refreshMemoStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally { setMemoBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="administracija-page">
      <div>
        <p className="text-sm text-muted-foreground uppercase tracking-wider">Administracija</p>
        <h1 className="serif text-3xl sm:text-4xl font-semibold">Administrativni alati</h1>
        <p className="text-muted-foreground mt-1">
          Audit log svih akcija u sustavu i alat za reset prije produkcije.
        </p>
      </div>

      <Tabs defaultValue="audit">
        <TabsList>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
          <TabsTrigger value="role-labels" data-testid="tab-role-labels">Nazivi uloga</TabsTrigger>
          <TabsTrigger value="custom-roles" data-testid="tab-custom-roles">Custom uloge</TabsTrigger>
          <TabsTrigger value="schedules" data-testid="tab-schedules">Radni rasporedi</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">Podsjetnici</TabsTrigger>
          {canManageStamp && <TabsTrigger value="stamp" data-testid="tab-stamp">Pečat dekana</TabsTrigger>}
          {canManageStamp && <TabsTrigger value="memorandum" data-testid="tab-memorandum">Memorandum</TabsTrigger>}
          {isAdmin && <TabsTrigger value="reset" data-testid="tab-reset">Reset podataka</TabsTrigger>}
        </TabsList>

        <TabsContent value="audit" className="space-y-4 mt-4">
          <Card>
            <CardContent className="p-3 flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Godišnji izvještaj (rektorat)</span>
              </div>
              <Select value={String(auditExportYear)} onValueChange={(v) => setAuditExportYear(parseInt(v, 10))}>
                <SelectTrigger className="w-[120px]" data-testid="audit-export-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[currentYear, currentYear - 1, currentYear - 2, currentYear - 3].map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={downloadAuditXlsx}
                disabled={auditExporting}
                size="sm"
                data-testid="audit-export-btn"
              >
                {auditExporting
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                Preuzmi Excel
              </Button>
              <p className="text-xs text-muted-foreground ml-auto max-w-md">
                Sva audit aktivnost (predaja, suglasnosti, generiranje rješenja, slanje) u 3 sheeta — kronologija, sažetak po vrsti akcije i mjesečni pregled.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3 flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Pretraži po e-mailu (npr. tomislav.pavkovic)"
                  value={filters.actor}
                  onChange={(e) => setFilters({ ...filters, actor: e.target.value })}
                  className="pl-9"
                  data-testid="audit-search"
                />
              </div>
              <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
                <SelectTrigger className="w-[220px]" data-testid="audit-action-filter">
                  <SelectValue placeholder="Sve akcije"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Sve akcije</SelectItem>
                  {actions.map(a => <SelectItem key={a} value={a}>{ACTION_LABEL[a] || a}</SelectItem>)}
                </SelectContent>
              </Select>
              {(filters.actor || filters.action !== "all") && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ actor: "", action: "all" })} data-testid="audit-clear">
                  <X className="w-4 h-4 mr-1"/>Očisti
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={loadAudit} data-testid="audit-refresh">
                <RefreshCw className="w-4 h-4 mr-1"/>Osvježi
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-xs uppercase">
                    <tr>
                      <th className="text-left p-3">Vrijeme</th>
                      <th className="text-left p-3">Korisnik</th>
                      <th className="text-left p-3">Akcija</th>
                      <th className="text-left p-3">Detalji</th>
                      <th className="text-left p-3">Zahtjev</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingAudit && (
                      <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Učitavanje…</td></tr>
                    )}
                    {!loadingAudit && audit.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nema zapisa za odabrane filtere.</td></tr>
                    )}
                    {audit.map(e => (
                      <tr key={e.id} className="border-t hover:bg-secondary/30" data-testid={`audit-row-${e.id}`}>
                        <td className="p-3 whitespace-nowrap text-xs">{hrDateTime(e.timestamp)}</td>
                        <td className="p-3">
                          <div className="font-medium text-xs">{e.actor_name || "—"}</div>
                          <div className="text-[11px] text-muted-foreground">{e.actor_email}</div>
                        </td>
                        <td className="p-3">
                          <Badge variant={e.action.startsWith("system") ? "destructive" : "secondary"} className="text-[11px]">
                            {ACTION_LABEL[e.action] || e.action}
                          </Badge>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground max-w-[300px] truncate" title={e.detail}>{e.detail || "—"}</td>
                        <td className="p-3 text-[11px] font-mono text-muted-foreground">{e.request_id === "system" ? "—" : e.request_id?.slice(0, 8)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {audit.length >= 200 && (
                <div className="p-3 text-center text-xs text-muted-foreground border-t">
                  Prikazano je 200 najnovijih zapisa. Suzite filtere za točan rezultat.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="role-labels" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Tag className="w-4 h-4 text-primary"/>Preimenovanje uloga
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Postavite vlastite nazive za 8 sistemskih uloga koji se prikazuju u sučelju.
                Ostavite prazno za zadani naziv. Sistemski identifikatori uloga (<code className="bg-secondary/60 px-1 rounded">admin</code>, <code className="bg-secondary/60 px-1 rounded">manager</code>…) ostaju nepromijenjeni — mijenja se samo prikaz.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(DEFAULT_ROLE_LABELS).map(([key, defaultLabel]) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-xs">
                      <span className="font-mono text-[10px] text-muted-foreground">{key}</span>
                      <span className="text-muted-foreground italic ml-2">(zadano: {defaultLabel})</span>
                    </Label>
                    <Input
                      value={labels[key] || ""}
                      placeholder={defaultLabel}
                      onChange={(e) => setLabels({ ...labels, [key]: e.target.value })}
                      data-testid={`role-label-${key}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button variant="outline" onClick={resetToDefaults} data-testid="reset-role-labels">
                  Očisti sve (vrati zadane)
                </Button>
                <Button onClick={saveLabels} disabled={savingLabels} data-testid="save-role-labels">
                  {savingLabels ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : null}
                  Spremi nazive
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground italic">
                Napomena: korisnici koji su trenutno prijavljeni vidjet će nove nazive nakon osvježavanja stranice.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="custom-roles" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <UserCog className="w-4 h-4 text-primary"/>Custom uloge (alias nad baznim ulogama)
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Dodajte vlastite uloge poput "Voditelj knjižnice" ili "Pomoćnik tajnika".
                Svaka custom uloga mapira se na <b>jednu od 8 baznih uloga</b> koja određuje stvarne ovlasti u sustavu.
                Dodjeljujete je zaposlenicima u modulu <i>Zaposlenici</i>.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {canManageCustomRoles && (
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Button onClick={openCrNew} data-testid="cr-add-btn">
                    <Plus className="w-4 h-4 mr-1"/>Dodaj novu ulogu
                  </Button>
                  <Button variant="outline" size="sm" onClick={loadCustomRoles} data-testid="cr-refresh-btn">
                    <RefreshCw className="w-4 h-4 mr-1"/>Osvježi
                  </Button>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-xs uppercase">
                    <tr>
                      <th className="text-left p-3">Naziv</th>
                      <th className="text-left p-3">Opis</th>
                      <th className="text-left p-3">Bazna uloga</th>
                      <th className="text-right p-3">Dodijeljeno</th>
                      {canManageCustomRoles && <th className="text-right p-3">Akcije</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {crLoading && (
                      <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Učitavanje…</td></tr>
                    )}
                    {!crLoading && customRoles.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">
                        Još nema custom uloga. Kliknite "Dodaj novu ulogu" za početak.
                      </td></tr>
                    )}
                    {customRoles.map(cr => (
                      <tr key={cr.id} className="border-t" data-testid={`cr-row-${cr.id}`}>
                        <td className="p-3 font-medium">{cr.name}</td>
                        <td className="p-3 text-xs text-muted-foreground max-w-[280px]">{cr.description || "—"}</td>
                        <td className="p-3">
                          <Badge variant="secondary" className="text-[11px]">
                            {ROLE_OPTIONS.find(o => o.value === cr.base_role)?.label || cr.base_role}
                          </Badge>
                        </td>
                        <td className="p-3 text-right text-xs">{cr.assigned_count || 0}</td>
                        {canManageCustomRoles && (
                          <td className="p-3 text-right space-x-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7"
                              onClick={() => openCrEdit(cr)}
                              data-testid={`cr-edit-${cr.id}`}>
                              <Pencil className="w-3.5 h-3.5"/>
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => { setCrDeleteFor(cr); setCrDeleteForce(false); }}
                              data-testid={`cr-delete-${cr.id}`}>
                              <Trash2 className="w-3.5 h-3.5"/>
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedules" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="w-4 h-4 text-primary"/>Radni rasporedi
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Definirajte raspored po smjenama (npr. 7-15, 8-16) ili "fleksibilan" raspored.
                Dodjeljuje se zaposleniku u modulu <i>Zaposlenici</i> → Uredi.
                Koristi se za izračun kašnjenja (max kašnjenja u min).
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {canManageSchedules && (
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Button onClick={openWsNew} data-testid="ws-add-btn">
                    <Plus className="w-4 h-4 mr-1"/>Dodaj raspored
                  </Button>
                  <Button variant="outline" size="sm" onClick={loadWorkSchedules} data-testid="ws-refresh-btn">
                    <RefreshCw className="w-4 h-4 mr-1"/>Osvježi
                  </Button>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-xs uppercase">
                    <tr>
                      <th className="text-left p-3">Naziv</th>
                      <th className="text-left p-3">Početak</th>
                      <th className="text-left p-3">Kraj</th>
                      <th className="text-right p-3">Max kašnjenje</th>
                      <th className="text-center p-3">Tip</th>
                      {canManageSchedules && <th className="text-right p-3">Akcije</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {wsLoading && (
                      <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Učitavanje…</td></tr>
                    )}
                    {!wsLoading && workSchedules.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                        Još nema definiranih rasporeda. Kliknite "Dodaj raspored".
                      </td></tr>
                    )}
                    {workSchedules.map(s => (
                      <tr key={s.id} className="border-t" data-testid={`ws-row-${s.id}`}>
                        <td className="p-3 font-medium">{s.name}</td>
                        <td className="p-3 text-xs">{s.flexible ? "—" : (s.start_time || "—")}</td>
                        <td className="p-3 text-xs">{s.flexible ? "—" : (s.end_time || "—")}</td>
                        <td className="p-3 text-right text-xs">{s.max_late_min ?? 15} min</td>
                        <td className="p-3 text-center">
                          {s.flexible
                            ? <Badge variant="outline" className="text-[10px]">Fleksibilan</Badge>
                            : <Badge variant="secondary" className="text-[10px]">Fiksan</Badge>}
                        </td>
                        {canManageSchedules && (
                          <td className="p-3 text-right space-x-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openWsEdit(s)} data-testid={`ws-edit-${s.id}`}>
                              <Pencil className="w-3.5 h-3.5"/>
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setWsDeleteFor(s)} data-testid={`ws-delete-${s.id}`}>
                              <Trash2 className="w-3.5 h-3.5"/>
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reminders" className="mt-4">
          <ReminderHistoryTab isAdmin={isAdmin}/>
        </TabsContent>

        {canManageStamp && (
          <TabsContent value="stamp" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Stamp className="w-4 h-4 text-primary"/>Pečat / potpis dekana
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Učitajte sliku pečata ili potpisa (PNG ili JPG, max 2MB). Slika se automatski koristi pri generiranju svih PDF rješenja (GO i WFH) — pojavljuje se iznad imena dekana u potpisnom bloku. Preporuka: prozirna pozadina (PNG), visina cca 80–120 px.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge variant={stampExists ? "default" : "outline"} className="text-xs" data-testid="stamp-status">
                    {stampExists ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Pečat postavljen — aktivan na PDF-u</> : <>Pečat još nije postavljen</>}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label htmlFor="stamp-upload">
                    <Button asChild disabled={stampBusy} data-testid="stamp-upload-btn">
                      <span className="cursor-pointer">
                        {stampBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Upload className="w-4 h-4 mr-2"/>}
                        {stampExists ? "Zamijeni pečat" : "Učitaj pečat"}
                      </span>
                    </Button>
                  </label>
                  <input
                    id="stamp-upload"
                    type="file"
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    className="hidden"
                    data-testid="stamp-upload-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      uploadStamp(f);
                      e.target.value = "";
                    }}
                  />
                  {stampExists && (
                    <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => setStampDeleteOpen(true)} disabled={stampBusy} data-testid="stamp-delete-btn">
                      <Trash2 className="w-4 h-4 mr-2"/>Ukloni pečat
                    </Button>
                  )}
                </div>
                {stampExists && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Trenutni pečat</Label>
                    <div className="bg-secondary/30 border rounded p-3 inline-block">
                      <StampPreview exists={stampExists}/>
                    </div>
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground border-t pt-3">
                  Akcije se bilježe u audit log (<code>dean_stamp_uploaded</code> / <code>dean_stamp_deleted</code>).
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canManageStamp && (
          <TabsContent value="memorandum" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileImage className="w-4 h-4 text-primary"/>Memorandum / zaglavlje PDF-a
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Učitajte sliku zaglavlja (npr. brendirani KIF memorandum s grbom i kontaktima — PNG ili JPG, max 5MB).
                  Slika <b>zamjenjuje zadano KIF logo zaglavlje</b> na svim sljedećim PDF rješenjima (godišnji odmor, rad na daljinu, povijest zahtjeva).
                  Preporuka: širina cca 17–19 cm, visina 2.5–3 cm, prozirna pozadina (PNG).
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge variant={memoExists ? "default" : "outline"} className="text-xs" data-testid="memo-status">
                    {memoExists
                      ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Memorandum postavljen — aktivan na PDF-u</>
                      : <>Memorandum nije postavljen (koristi se zadano KIF zaglavlje)</>}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label htmlFor="memo-upload">
                    <Button asChild disabled={memoBusy} data-testid="memo-upload-btn">
                      <span className="cursor-pointer">
                        {memoBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Upload className="w-4 h-4 mr-2"/>}
                        {memoExists ? "Zamijeni memorandum" : "Učitaj memorandum"}
                      </span>
                    </Button>
                  </label>
                  <input
                    id="memo-upload"
                    type="file"
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    className="hidden"
                    data-testid="memo-upload-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      uploadMemo(f);
                      e.target.value = "";
                    }}
                  />
                  {memoExists && (
                    <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => setMemoDeleteOpen(true)} disabled={memoBusy} data-testid="memo-delete-btn">
                      <Trash2 className="w-4 h-4 mr-2"/>Vrati zadano (KIF logo)
                    </Button>
                  )}
                </div>
                {memoExists && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Trenutni memorandum</Label>
                    <div className="bg-secondary/30 border rounded p-3 inline-block max-w-full overflow-hidden">
                      <StampPreview
                        exists={memoExists}
                        endpoint="/admin/memorandum/preview"
                        testid="memo-preview"
                        maxHeight={160}
                        maxWidth={720}
                      />
                    </div>
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground border-t pt-3">
                  Akcije se bilježe u audit log (<code>memorandum_uploaded</code> / <code>memorandum_deleted</code>).
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="reset" className="mt-4 space-y-4">
            {/* Iter68: Snapshot osnovne baze */}
            <Card className="border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DatabaseBackup className="w-5 h-5"/> Osnovna baza (snapshot)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-secondary/60 border border-border rounded p-3 space-y-2 text-sm">
                  <p>Snapshot sprema <b>trenutno stanje zaposlenika</b> (uloge, fondovi, manageri, kartice), <b>custom rola, postavki i rasporeda</b> kao osnovnu bazu sustava.
                     "BRISI SVE" vraća bazu točno na ovaj snapshot.</p>
                  <p className="text-xs text-muted-foreground">
                    Radni tok za produkciju: uredite podatke ovdje → <b>Spremi snapshot</b> → <b>Deploy</b> → u produkciji pokrenite "BRISI SVE".
                  </p>
                </div>
                {snapInfo?.exists ? (
                  <p className="text-sm" data-testid="snapshot-info">
                    <CheckCircle2 className="w-4 h-4 inline text-emerald-600 mr-1"/>
                    Zadnji snapshot: <b>{new Date(snapInfo.created_at).toLocaleString("hr-HR")}</b>
                    {" · "}{snapInfo.counts?.users ?? "?"} zaposlenika · {snapInfo.counts?.custom_roles ?? 0} custom rola
                    {snapInfo.saved_by ? ` · spremio: ${snapInfo.saved_by}` : ""}
                  </p>
                ) : (
                  <p className="text-sm text-amber-700" data-testid="snapshot-info">
                    <AlertTriangle className="w-4 h-4 inline mr-1"/>Snapshot još nije spremljen — "BRISI SVE" neće dirati zaposlenike ni postavke.
                  </p>
                )}
                <Button onClick={saveSnapshot} disabled={snapSaving} data-testid="snapshot-save-btn">
                  {snapSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <DatabaseBackup className="w-4 h-4 mr-2"/>}
                  Spremi trenutno stanje kao osnovnu bazu
                </Button>
              </CardContent>
            </Card>

            <Card className="border-red-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <ShieldAlert className="w-5 h-5"/> Reset zahtjeva i audit zapisa
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-red-50 border border-red-200 rounded p-3 space-y-2 text-sm">
                  <p className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0"/>
                    <span><b>Opasno!</b> Ova radnja <b>nepovratno briše</b> sve zahtjeve za godišnji odmor, sve WFH zahtjeve, audit zapise, obavijesti i podsjetnike,
                      te <b>vraća zaposlenike, custom role i postavke na spremljeni snapshot</b>. <b>Evidencija prisutnosti (RFID) se čuva.</b></span>
                  </p>
                  <p className="text-xs text-red-700">
                    Koristite ovo prije puštanja sustava u produkciju, kako biste obrisali sve testne podatke i krenuli "od nule" s osnovnom bazom.
                  </p>
                </div>
                <Button variant="destructive" onClick={() => setResetOpen(true)} data-testid="open-reset-btn">
                  <Trash2 className="w-4 h-4 mr-2"/>Obriši sve zahtjeve i audit
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={resetOpen} onOpenChange={(v) => { if (!v) { setResetOpen(false); setResetPhrase(""); }}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5"/> Potvrda brisanja
            </DialogTitle>
            <DialogDescription>Potrebna je potvrdna fraza za nepovratno brisanje podataka.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Ovo će <b>nepovratno obrisati</b> sve zahtjeve, audit zapise, obavijesti i podsjetnike te <b>vratiti zaposlenike i postavke na spremljeni snapshot</b>. Evidencija prisutnosti se čuva.</p>
            <div className="space-y-1">
              <Label>Za potvrdu upišite <code className="bg-red-100 px-1 rounded">BRISI SVE</code> velikim slovima:</Label>
              <Input
                value={resetPhrase}
                onChange={(e) => setResetPhrase(e.target.value)}
                placeholder="BRISI SVE"
                data-testid="reset-phrase-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetOpen(false); setResetPhrase(""); }} data-testid="reset-cancel-btn">
              Odustani
            </Button>
            <Button
              variant="destructive"
              onClick={doReset}
              disabled={resetPhrase !== "BRISI SVE" || resetting}
              data-testid="reset-confirm-btn"
            >
              {resetting && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}
              Obriši sve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Role create/edit dialog */}
      <Dialog open={!!crDialog} onOpenChange={(v) => { if (!v) setCrDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {crDialog === "new" ? "Nova custom uloga" : `Uredi ulogu: ${crDialog?.name || ""}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Naziv uloge <span className="text-red-600">*</span></Label>
              <Input
                value={crForm.name}
                onChange={(e) => setCrForm({ ...crForm, name: e.target.value })}
                placeholder="npr. Voditelj knjižnice"
                maxLength={80}
                data-testid="cr-form-name"
              />
            </div>
            <div className="space-y-1">
              <Label>Opis (opcionalno)</Label>
              <Textarea
                value={crForm.description}
                onChange={(e) => setCrForm({ ...crForm, description: e.target.value })}
                placeholder="Kratak opis ovlasti / odgovornosti"
                rows={3}
                maxLength={300}
                data-testid="cr-form-description"
              />
              <p className="text-[11px] text-muted-foreground">{(crForm.description || "").length}/300</p>
            </div>
            <div className="space-y-1">
              <Label>Bazna uloga (određuje ovlasti) <span className="text-red-600">*</span></Label>
              <Select value={crForm.base_role} onValueChange={(v) => setCrForm({ ...crForm, base_role: v })}>
                <SelectTrigger data-testid="cr-form-base-role">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Korisnici s ovom custom ulogom imat će ovlasti odabrane bazne uloge. Sustav ostaje siguran — RBAC se ne mijenja.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrDialog(null)}>Odustani</Button>
            <Button onClick={saveCustomRole} disabled={crBusy || !crForm.name?.trim()} data-testid="cr-form-save">
              {crBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : null}
              Spremi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Role delete confirm */}
      <Dialog open={!!crDeleteFor} onOpenChange={(v) => { if (!v) { setCrDeleteFor(null); setCrDeleteForce(false); }}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600"/>
              Brisanje uloge: {crDeleteFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            {crDeleteFor?.assigned_count > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-800 text-xs">
                <b>Pažnja:</b> ova uloga je dodijeljena <b>{crDeleteFor.assigned_count}</b> zaposleniku(ima).
                Nakon brisanja oni zadržavaju samo svoju baznu ulogu.
              </div>
            ) : (
              <p>Sigurni ste da želite obrisati ovu ulogu?</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCrDeleteFor(null); setCrDeleteForce(false); }}>Odustani</Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteCr}
              disabled={crBusy}
              data-testid="cr-delete-confirm">
              {crBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}
              {crDeleteFor?.assigned_count > 0 ? "Obriši i odjavi korisnike" : "Obriši"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Work schedule create/edit dialog */}
      <Dialog open={!!wsDialog} onOpenChange={(v) => { if (!v) setWsDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {wsDialog === "new" ? "Novi raspored rada" : `Uredi raspored: ${wsDialog?.name || ""}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Naziv <span className="text-red-600">*</span></Label>
              <Input
                value={wsForm.name}
                onChange={(e) => setWsForm({ ...wsForm, name: e.target.value })}
                placeholder="npr. 7:00-15:00 ili Fleksibilan"
                maxLength={60}
                data-testid="ws-form-name"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={wsForm.flexible}
                onChange={(e) => setWsForm({ ...wsForm, flexible: e.target.checked })}
                id="ws-flex"
                data-testid="ws-form-flexible"
                className="w-4 h-4"
              />
              <Label htmlFor="ws-flex" className="cursor-pointer">Fleksibilan raspored (bez fiksnog početka/kraja)</Label>
            </div>
            {!wsForm.flexible && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Početak</Label>
                  <Input type="time" value={wsForm.start_time}
                    onChange={(e) => setWsForm({ ...wsForm, start_time: e.target.value })}
                    data-testid="ws-form-start"/>
                </div>
                <div className="space-y-1">
                  <Label>Kraj</Label>
                  <Input type="time" value={wsForm.end_time}
                    onChange={(e) => setWsForm({ ...wsForm, end_time: e.target.value })}
                    data-testid="ws-form-end"/>
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label>Max kašnjenje (minuta)</Label>
              <Input type="number" min="0" max="120"
                value={wsForm.max_late_min}
                onChange={(e) => setWsForm({ ...wsForm, max_late_min: Number(e.target.value) })}
                data-testid="ws-form-max-late"/>
              <p className="text-[11px] text-muted-foreground">
                Dolasci unutar ovog praga nakon početka rasporeda <b>neće</b> biti označeni kao kašnjenje.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWsDialog(null)}>Odustani</Button>
            <Button onClick={saveWorkSchedule} disabled={wsBusy || !wsForm.name?.trim()} data-testid="ws-form-save">
              {wsBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}Spremi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Work schedule delete confirm */}
      <Dialog open={!!wsDeleteFor} onOpenChange={(v) => { if (!v) setWsDeleteFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600"/>
              Brisanje rasporeda: {wsDeleteFor?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Sigurni ste da želite obrisati ovaj raspored? Ako je dodijeljen zaposlenicima, sustav će vratiti grešku.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWsDeleteFor(null)}>Odustani</Button>
            <Button variant="destructive" onClick={confirmDeleteWs} disabled={wsBusy} data-testid="ws-delete-confirm">
              {wsBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}Obriši
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dean stamp delete confirm */}
      <Dialog open={stampDeleteOpen} onOpenChange={(v) => { if (!v) setStampDeleteOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5"/>Ukloni pečat dekana
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Sigurni ste da želite ukloniti spremljeni pečat? Sljedeća PDF rješenja neće sadržavati sliku potpisa dok ne učitate novu.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStampDeleteOpen(false)} data-testid="stamp-delete-cancel">Odustani</Button>
            <Button variant="destructive" onClick={deleteStamp} disabled={stampBusy} data-testid="stamp-delete-confirm">
              {stampBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}Ukloni
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Memorandum delete confirm */}
      <Dialog open={memoDeleteOpen} onOpenChange={(v) => { if (!v) setMemoDeleteOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5"/>Vrati zadano zaglavlje
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Sigurni ste da želite ukloniti memorandum? Sljedeća PDF rješenja koristit će zadano KIF logo zaglavlje.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemoDeleteOpen(false)} data-testid="memo-delete-cancel">Odustani</Button>
            <Button variant="destructive" onClick={deleteMemo} disabled={memoBusy} data-testid="memo-delete-confirm">
              {memoBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}Ukloni
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
